import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, ChevronsUpDown, Tag, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface OrderFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuItem?: {
    id: string;
    name: string;
    price: number;
  };
}

const OrderFormModal = ({ open, onOpenChange, menuItem }: OrderFormModalProps) => {
  const [user, setUser] = useState<any>(null);
  const [countryCodeOpen, setCountryCodeOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    countryCode: "",
    phone: "",
    address: "",
    quantity: 1,
    notes: "",
    paymentMethod: "cash_on_delivery",
    gcashReference: "",
    paymentProof: null as File | null,
    referredByPhone: "",
    promoCode: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{code: string; discount: number} | null>(null);
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const { toast } = useToast();

  const countries = [
    { code: "+93", name: "Afghanistan", flag: "🇦🇫" },
    { code: "+355", name: "Albania", flag: "🇦🇱" },
    { code: "+213", name: "Algeria", flag: "🇩🇿" },
    { code: "+376", name: "Andorra", flag: "🇦🇩" },
    { code: "+244", name: "Angola", flag: "🇦🇴" },
    { code: "+54", name: "Argentina", flag: "🇦🇷" },
    { code: "+374", name: "Armenia", flag: "🇦🇲" },
    { code: "+61", name: "Australia", flag: "🇦🇺" },
    { code: "+43", name: "Austria", flag: "🇦🇹" },
    { code: "+994", name: "Azerbaijan", flag: "🇦🇿" },
    { code: "+973", name: "Bahrain", flag: "🇧🇭" },
    { code: "+880", name: "Bangladesh", flag: "🇧🇩" },
    { code: "+375", name: "Belarus", flag: "🇧🇾" },
    { code: "+32", name: "Belgium", flag: "🇧🇪" },
    { code: "+501", name: "Belize", flag: "🇧🇿" },
    { code: "+229", name: "Benin", flag: "🇧🇯" },
    { code: "+975", name: "Bhutan", flag: "🇧🇹" },
    { code: "+591", name: "Bolivia", flag: "🇧🇴" },
    { code: "+387", name: "Bosnia", flag: "🇧🇦" },
    { code: "+267", name: "Botswana", flag: "🇧🇼" },
    { code: "+55", name: "Brazil", flag: "🇧🇷" },
    { code: "+673", name: "Brunei", flag: "🇧🇳" },
    { code: "+359", name: "Bulgaria", flag: "🇧🇬" },
    { code: "+226", name: "Burkina Faso", flag: "🇧🇫" },
    { code: "+257", name: "Burundi", flag: "🇧🇮" },
    { code: "+855", name: "Cambodia", flag: "🇰🇭" },
    { code: "+237", name: "Cameroon", flag: "🇨🇲" },
    { code: "+1", name: "Canada", flag: "🇨🇦" },
    { code: "+56", name: "Chile", flag: "🇨🇱" },
    { code: "+86", name: "China", flag: "🇨🇳" },
    { code: "+57", name: "Colombia", flag: "🇨🇴" },
    { code: "+506", name: "Costa Rica", flag: "🇨🇷" },
    { code: "+385", name: "Croatia", flag: "🇭🇷" },
    { code: "+53", name: "Cuba", flag: "🇨🇺" },
    { code: "+357", name: "Cyprus", flag: "🇨🇾" },
    { code: "+420", name: "Czech Republic", flag: "🇨🇿" },
    { code: "+45", name: "Denmark", flag: "🇩🇰" },
    { code: "+253", name: "Djibouti", flag: "🇩🇯" },
    { code: "+593", name: "Ecuador", flag: "🇪🇨" },
    { code: "+20", name: "Egypt", flag: "🇪🇬" },
    { code: "+503", name: "El Salvador", flag: "🇸🇻" },
    { code: "+372", name: "Estonia", flag: "🇪🇪" },
    { code: "+251", name: "Ethiopia", flag: "🇪🇹" },
    { code: "+358", name: "Finland", flag: "🇫🇮" },
    { code: "+33", name: "France", flag: "🇫🇷" },
    { code: "+995", name: "Georgia", flag: "🇬🇪" },
    { code: "+49", name: "Germany", flag: "🇩🇪" },
    { code: "+233", name: "Ghana", flag: "🇬🇭" },
    { code: "+30", name: "Greece", flag: "🇬🇷" },
    { code: "+502", name: "Guatemala", flag: "🇬🇹" },
    { code: "+224", name: "Guinea", flag: "🇬🇳" },
    { code: "+509", name: "Haiti", flag: "🇭🇹" },
    { code: "+504", name: "Honduras", flag: "🇭🇳" },
    { code: "+852", name: "Hong Kong", flag: "🇭🇰" },
    { code: "+36", name: "Hungary", flag: "🇭🇺" },
    { code: "+354", name: "Iceland", flag: "🇮🇸" },
    { code: "+91", name: "India", flag: "🇮🇳" },
    { code: "+62", name: "Indonesia", flag: "🇮🇩" },
    { code: "+98", name: "Iran", flag: "🇮🇷" },
    { code: "+964", name: "Iraq", flag: "🇮🇶" },
    { code: "+353", name: "Ireland", flag: "🇮🇪" },
    { code: "+972", name: "Israel", flag: "🇮🇱" },
    { code: "+39", name: "Italy", flag: "🇮🇹" },
    { code: "+81", name: "Japan", flag: "🇯🇵" },
    { code: "+962", name: "Jordan", flag: "🇯🇴" },
    { code: "+7", name: "Kazakhstan", flag: "🇰🇿" },
    { code: "+254", name: "Kenya", flag: "🇰🇪" },
    { code: "+965", name: "Kuwait", flag: "🇰🇼" },
    { code: "+996", name: "Kyrgyzstan", flag: "🇰🇬" },
    { code: "+856", name: "Laos", flag: "🇱🇦" },
    { code: "+371", name: "Latvia", flag: "🇱🇻" },
    { code: "+961", name: "Lebanon", flag: "🇱🇧" },
    { code: "+266", name: "Lesotho", flag: "🇱🇸" },
    { code: "+231", name: "Liberia", flag: "🇱🇷" },
    { code: "+218", name: "Libya", flag: "🇱🇾" },
    { code: "+370", name: "Lithuania", flag: "🇱🇹" },
    { code: "+352", name: "Luxembourg", flag: "🇱🇺" },
    { code: "+853", name: "Macau", flag: "🇲🇴" },
    { code: "+261", name: "Madagascar", flag: "🇲🇬" },
    { code: "+265", name: "Malawi", flag: "🇲🇼" },
    { code: "+60", name: "Malaysia", flag: "🇲🇾" },
    { code: "+960", name: "Maldives", flag: "🇲🇻" },
    { code: "+223", name: "Mali", flag: "🇲🇱" },
    { code: "+356", name: "Malta", flag: "🇲🇹" },
    { code: "+222", name: "Mauritania", flag: "🇲🇷" },
    { code: "+230", name: "Mauritius", flag: "🇲🇺" },
    { code: "+52", name: "Mexico", flag: "🇲🇽" },
    { code: "+373", name: "Moldova", flag: "🇲🇩" },
    { code: "+377", name: "Monaco", flag: "🇲🇨" },
    { code: "+976", name: "Mongolia", flag: "🇲🇳" },
    { code: "+382", name: "Montenegro", flag: "🇲🇪" },
    { code: "+212", name: "Morocco", flag: "🇲🇦" },
    { code: "+258", name: "Mozambique", flag: "🇲🇿" },
    { code: "+95", name: "Myanmar", flag: "🇲🇲" },
    { code: "+264", name: "Namibia", flag: "🇳🇦" },
    { code: "+977", name: "Nepal", flag: "🇳🇵" },
    { code: "+31", name: "Netherlands", flag: "🇳🇱" },
    { code: "+64", name: "New Zealand", flag: "🇳🇿" },
    { code: "+505", name: "Nicaragua", flag: "🇳🇮" },
    { code: "+227", name: "Niger", flag: "🇳🇪" },
    { code: "+234", name: "Nigeria", flag: "🇳🇬" },
    { code: "+850", name: "North Korea", flag: "🇰🇵" },
    { code: "+47", name: "Norway", flag: "🇳🇴" },
    { code: "+968", name: "Oman", flag: "🇴🇲" },
    { code: "+92", name: "Pakistan", flag: "🇵🇰" },
    { code: "+970", name: "Palestine", flag: "🇵🇸" },
    { code: "+507", name: "Panama", flag: "🇵🇦" },
    { code: "+595", name: "Paraguay", flag: "🇵🇾" },
    { code: "+51", name: "Peru", flag: "🇵🇪" },
    { code: "+63", name: "Philippines", flag: "🇵🇭" },
    { code: "+48", name: "Poland", flag: "🇵🇱" },
    { code: "+351", name: "Portugal", flag: "🇵🇹" },
    { code: "+974", name: "Qatar", flag: "🇶🇦" },
    { code: "+40", name: "Romania", flag: "🇷🇴" },
    { code: "+7", name: "Russia", flag: "🇷🇺" },
    { code: "+250", name: "Rwanda", flag: "🇷🇼" },
    { code: "+966", name: "Saudi Arabia", flag: "🇸🇦" },
    { code: "+221", name: "Senegal", flag: "🇸🇳" },
    { code: "+381", name: "Serbia", flag: "🇷🇸" },
    { code: "+65", name: "Singapore", flag: "🇸🇬" },
    { code: "+421", name: "Slovakia", flag: "🇸🇰" },
    { code: "+386", name: "Slovenia", flag: "🇸🇮" },
    { code: "+252", name: "Somalia", flag: "🇸🇴" },
    { code: "+27", name: "South Africa", flag: "🇿🇦" },
    { code: "+82", name: "South Korea", flag: "🇰🇷" },
    { code: "+211", name: "South Sudan", flag: "🇸🇸" },
    { code: "+34", name: "Spain", flag: "🇪🇸" },
    { code: "+94", name: "Sri Lanka", flag: "🇱🇰" },
    { code: "+249", name: "Sudan", flag: "🇸🇩" },
    { code: "+46", name: "Sweden", flag: "🇸🇪" },
    { code: "+41", name: "Switzerland", flag: "🇨🇭" },
    { code: "+963", name: "Syria", flag: "🇸🇾" },
    { code: "+886", name: "Taiwan", flag: "🇹🇼" },
    { code: "+992", name: "Tajikistan", flag: "🇹🇯" },
    { code: "+255", name: "Tanzania", flag: "🇹🇿" },
    { code: "+66", name: "Thailand", flag: "🇹🇭" },
    { code: "+228", name: "Togo", flag: "🇹🇬" },
    { code: "+216", name: "Tunisia", flag: "🇹🇳" },
    { code: "+90", name: "Turkey", flag: "🇹🇷" },
    { code: "+993", name: "Turkmenistan", flag: "🇹🇲" },
    { code: "+256", name: "Uganda", flag: "🇺🇬" },
    { code: "+380", name: "Ukraine", flag: "🇺🇦" },
    { code: "+971", name: "UAE", flag: "🇦🇪" },
    { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
    { code: "+1", name: "United States", flag: "🇺🇸" },
    { code: "+598", name: "Uruguay", flag: "🇺🇾" },
    { code: "+998", name: "Uzbekistan", flag: "🇺🇿" },
    { code: "+58", name: "Venezuela", flag: "🇻🇪" },
    { code: "+84", name: "Vietnam", flag: "🇻🇳" },
    { code: "+967", name: "Yemen", flag: "🇾🇪" },
    { code: "+260", name: "Zambia", flag: "🇿🇲" },
    { code: "+263", name: "Zimbabwe", flag: "🇿🇼" },
  ];

  // Check auth and load user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        
        // Check if user has previous orders (existing customer)
        const { count: orderCount } = await supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', session.user.id);
        
        setIsNewCustomer(orderCount === 0 || orderCount === null);
        
        // Try to fetch user profile first
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        
        // Use profile data if available, otherwise fall back to user metadata
        const userMetadata = session.user.user_metadata || {};
        const fullName = profile?.full_name || userMetadata.full_name || "";
        const phoneFromDb = profile?.phone || userMetadata.phone || "";
        const addressFromDb = profile?.address || "";
        
        // If profile doesn't exist, create it from user metadata
        if (!profile && (userMetadata.full_name || userMetadata.phone)) {
          await supabase
            .from('profiles')
            .upsert({
              id: session.user.id,
              full_name: userMetadata.full_name || "",
              phone: userMetadata.phone || "",
              address: ""
            }, { onConflict: 'id' });
        }
        
        // Extract country code from phone if it exists
        let phoneNumber = phoneFromDb.trim();
        let countryCode = "+63"; // Default to Philippines
        
        if (phoneNumber) {
          // Check if phone starts with +
          if (phoneNumber.startsWith('+')) {
            // Try to match known country codes (prioritize longer codes first)
            const sortedCountries = [...countries].sort((a, b) => b.code.length - a.code.length);
            for (const country of sortedCountries) {
              if (phoneNumber.startsWith(country.code)) {
                countryCode = country.code;
                phoneNumber = phoneNumber.slice(country.code.length);
                break;
              }
            }
          } else if (phoneNumber.startsWith('63')) {
            // Handle phone numbers starting with 63 without +
            countryCode = "+63";
            phoneNumber = phoneNumber.slice(2);
          } else if (phoneNumber.startsWith('0')) {
            // Handle Philippine local format (0917...)
            countryCode = "+63";
            phoneNumber = phoneNumber.slice(1);
          }
          // If no prefix detected, keep the number as is with default +63
        }
        
        setFormData(prev => ({
          ...prev,
          name: fullName,
          countryCode: countryCode,
          phone: phoneNumber,
          address: addressFromDb
        }));
      }
    };
    
    if (open) {
      loadUserProfile();
    }
  }, [open]);

  // Default menu item if none provided
  const selectedItem = menuItem || {
    id: "default",
    name: "Hyderabadi Bawarchi Chicken Biryani",
    price: 149
  };

  // Calculate total with discount
  const subtotal = selectedItem.price * formData.quantity;
  const discount = appliedPromo?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  // Validate promo code
  const validatePromoCode = async () => {
    if (!formData.promoCode.trim()) {
      toast({
        title: "Enter Promo Code",
        description: "Please enter a promo code to apply.",
        variant: "destructive",
      });
      return;
    }

    setPromoValidating(true);
    try {
      const { data: promo, error } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('code', formData.promoCode.trim().toUpperCase())
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString())
        .single();

      if (error || !promo) {
        toast({
          title: "Invalid Promo Code",
          description: "This promo code is invalid or has expired.",
          variant: "destructive",
        });
        return;
      }

      setAppliedPromo({
        code: promo.code,
        discount: promo.discount_amount
      });
      
      toast({
        title: "Promo Code Applied!",
        description: `₱${promo.discount_amount} discount applied to your order.`,
      });
    } catch (error) {
      console.error('Promo validation error:', error);
      toast({
        title: "Error",
        description: "Failed to validate promo code.",
        variant: "destructive",
      });
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setFormData({ ...formData, promoCode: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Require authentication
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to place an order.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.countryCode) {
      toast({
        title: "Country Code Required",
        description: "Please select your country code before placing the order.",
        variant: "destructive",
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      let paymentProofUrl = null;
      
      // Upload payment proof if GCash is selected
      if (formData.paymentMethod === 'gcash' && formData.paymentProof && user) {
        const fileExt = formData.paymentProof.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('payment-proofs')
          .upload(fileName, formData.paymentProof);
        
        if (uploadError) {
          throw new Error(`Failed to upload payment proof: ${uploadError.message}`);
        }
        
        const { data: { publicUrl } } = supabase.storage
          .from('payment-proofs')
          .getPublicUrl(fileName);
        
        paymentProofUrl = publicUrl;
      }
      
      const orderDetails = {
        name: formData.name.trim(),
        phone: `${formData.countryCode}${formData.phone.trim()}`,
        address: formData.address.trim(),
        quantity: formData.quantity,
        notes: formData.notes.trim() || null,
        menuItemName: selectedItem.name,
        totalPrice: total,
        paymentMethod: formData.paymentMethod,
        gcashReference: formData.paymentMethod === 'gcash' ? formData.gcashReference.trim() : null,
        paymentProofUrl: paymentProofUrl,
        referredByPhone: formData.referredByPhone.trim() || null,
        promoCode: appliedPromo?.code || null,
        promoDiscount: appliedPromo?.discount || null
      };

      // Save order directly to database
      const { data, error } = await supabase.functions.invoke('submit-order', {
        body: orderDetails
      });

      if (error) throw error;

      // Auto-save updated customer details to profile
      if (user) {
        const fullPhone = `${formData.countryCode}${formData.phone.trim()}`;
        await supabase
          .from('profiles')
          .upsert({
            id: user.id,
            full_name: formData.name.trim(),
            phone: fullPhone,
            address: formData.address.trim()
          }, { onConflict: 'id' });
      }

      toast({
        title: "Order Placed Successfully!",
        description: "Your order has been received and will be processed soon.",
      });
      
      // Reset form and close modal
      onOpenChange(false);
      setFormData({ 
        name: "", 
        countryCode: "",
        phone: "", 
        address: "", 
        quantity: 1, 
        notes: "",
        paymentMethod: "cash_on_delivery",
        gcashReference: "",
        paymentProof: null,
        referredByPhone: "",
        promoCode: ""
      });
      setAppliedPromo(null);
      
    } catch (error: any) {
      console.error('Order submission error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Place Your Order</DialogTitle>
          <DialogDescription>
            {!user ? (
              "Please sign in to place your order. If you don't want to sign up, use the WhatsApp option below."
            ) : (
              "Fill in your details to place your order"
            )}
          </DialogDescription>
        </DialogHeader>
        
        {!user ? (
          <div className="space-y-4 py-6">
            <p className="text-center text-muted-foreground">
              You need to be signed in to place orders through this form.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => {
                onOpenChange(false);
                window.location.href = '/customer-auth';
              }} className="w-full">
                Sign In / Sign Up
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  onOpenChange(false);
                  window.open(`https://wa.me/639953947586?text=Hi, I'd like to order ${selectedItem.name}`, '_blank');
                }}
                className="w-full"
              >
                Order via WhatsApp for pre and bulk orders
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 px-1">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                maxLength={100}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="flex gap-2">
              <Popover open={countryCodeOpen} onOpenChange={setCountryCodeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={countryCodeOpen}
                    className="w-[180px] justify-between"
                  >
                    {formData.countryCode
                      ? countries.find((country) => country.code === formData.countryCode)
                        ? `${countries.find((country) => country.code === formData.countryCode)?.flag} ${formData.countryCode}`
                        : "Select country"
                      : "Select country"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0 bg-background" align="start">
                  <Command>
                    <CommandInput placeholder="Search country or +code..." />
                    <CommandList>
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup>
                        {countries.map((country) => (
                          <CommandItem
                            key={country.code + country.name}
                            value={`${country.name} ${country.code}`}
                            onSelect={() => {
                              setFormData({ ...formData, countryCode: country.code });
                              setCountryCodeOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.countryCode === country.code ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {country.flag} {country.name} {country.code}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Input
                id="phone"
                type="tel"
                required
                minLength={6}
                maxLength={15}
                pattern="[0-9]{6,15}"
                className="flex-1"
                value={formData.phone}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.startsWith('0')) {
                    value = value.substring(1);
                  }
                  setFormData({ ...formData, phone: value });
                }}
                placeholder="9953947586"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Search by country name or code (e.g., "India" or "+91") and enter phone number
            </p>
          </div>

            <div className="space-y-2">
              <Label htmlFor="address">Delivery Address *</Label>
              <Textarea
                id="address"
                required
                minLength={5}
                maxLength={500}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter your complete delivery address in Makati"
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                max="100"
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
              />
            </div>

            <div className="space-y-3">
              <Label>Payment Method *</Label>
              <RadioGroup
                value={formData.paymentMethod}
                onValueChange={(value) => setFormData({ ...formData, paymentMethod: value })}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cash_on_delivery" id="cod" />
                  <Label htmlFor="cod" className="font-normal cursor-pointer">
                    Cash on Delivery
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="gcash" id="gcash" />
                  <Label htmlFor="gcash" className="font-normal cursor-pointer">
                    GCash (Pay via 0995 394 7586)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {formData.paymentMethod === 'gcash' && (
              <div className="space-y-3 p-3 bg-muted rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="gcashReference">GCash Reference Number *</Label>
                  <Input
                    id="gcashReference"
                    required={formData.paymentMethod === 'gcash'}
                    value={formData.gcashReference}
                    onChange={(e) => setFormData({ ...formData, gcashReference: e.target.value })}
                    placeholder="Enter 13-digit reference number"
                    maxLength={13}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="paymentProof">Payment Screenshot/Receipt *</Label>
                  <Input
                    id="paymentProof"
                    type="file"
                    accept="image/*"
                    required={formData.paymentMethod === 'gcash'}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setFormData({ ...formData, paymentProof: file });
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Upload a screenshot or photo of your GCash payment receipt
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Send payment to GCash number 0995 394 7586
                </p>
              </div>
            )}

            {/* Referral Section - Only for new customers */}
            {isNewCustomer && (
              <div className="space-y-2">
                <Label htmlFor="referredByPhone">Referred by (Optional)</Label>
                <Input
                  id="referredByPhone"
                  type="tel"
                  maxLength={20}
                  value={formData.referredByPhone}
                  onChange={(e) => setFormData({ ...formData, referredByPhone: e.target.value.replace(/[^0-9+]/g, '') })}
                  placeholder="e.g., +639171234567"
                />
                <p className="text-xs text-muted-foreground">
                  Enter referrer's phone with country code (e.g., +63 for Philippines). They'll get ₱50 off!
                </p>
              </div>
            )}

            {/* Promo Code Section */}
            <div className="space-y-2">
              <Label htmlFor="promoCode">Promo Code (Optional)</Label>
              {appliedPromo ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <Tag className="h-4 w-4 text-green-600" />
                  <span className="flex-1 text-green-700 dark:text-green-300 font-medium">
                    {appliedPromo.code} - ₱{appliedPromo.discount} off
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removePromo}
                    className="h-8 w-8 p-0 text-green-600 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    id="promoCode"
                    value={formData.promoCode}
                    onChange={(e) => setFormData({ ...formData, promoCode: e.target.value.toUpperCase() })}
                    placeholder="Enter promo code"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validatePromoCode}
                    disabled={promoValidating}
                  >
                    {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Special Instructions (Optional)</Label>
              <Textarea
                id="notes"
                maxLength={1000}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special requests or notes"
                className="min-h-[60px]"
              />
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>Subtotal:</span>
                <span>₱{subtotal}</span>
              </div>
              {appliedPromo && (
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>Promo Discount:</span>
                  <span>-₱{appliedPromo.discount}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-semibold">Total:</span>
                <span className="text-2xl font-bold text-primary">₱{total}</span>
              </div>
            </div>

            <div className="sticky bottom-0 bg-background pt-4 pb-2 -mx-1 px-1 space-y-2">
              <p className="text-xs text-muted-foreground text-center">
                * Delivery charges at actual
              </p>
              <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                {submitting ? "Placing Order..." : "Place Order"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderFormModal;
